import React, { useState } from "react";
import { Tag, Button, Space, Popconfirm, message, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { TeamOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { WebApiMemberDtoMemberDto } from "../../Repository/eventbookyapi";
import { familyDisplayName, wantsNoEmail } from "../../Helpers/FamilyMemberHelper";
import { TextService } from "../../Services/TextService";
import { MemberService } from "../../Services/MemberService";
import FamilyMemberForm from "../Users/FamilyMemberForm";

interface FamilyMemberSubListProps {
    primaryMember: WebApiMemberDtoMemberDto;
    familyMembers: WebApiMemberDtoMemberDto[];
    /** Lower-cased free-text search, used to highlight the matching family member(s). */
    searchLower?: string;
    canEdit?: boolean;
    groups?: any[];
    loadMembers?: () => void;
    /** Hide the built-in "Familjemedlemmar" heading row (e.g. when the parent already has a section title). */
    hideHeader?: boolean;
}

const rowMatches = (m: WebApiMemberDtoMemberDto, searchLower?: string): boolean => {
    if (!searchLower) return false;
    return `${m.firstName ?? ""} ${m.lastName ?? ""} ${m.email ?? ""} ${m.phoneNumber ?? ""}`
        .toLowerCase()
        .includes(searchLower);
};

const highlight = (text: string | null | undefined, searchLower?: string): React.ReactNode => {
    if (!text) return null;
    if (searchLower && text.toLowerCase().includes(searchLower)) return <mark>{text}</mark>;
    return text;
};

export const FamilyMemberSubList: React.FC<FamilyMemberSubListProps> = (props) => {
    const GetText = TextService.GetText;
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<WebApiMemberDtoMemberDto | undefined>(undefined);
    const [busyId, setBusyId] = useState<number | null>(null);

    const openAdd = () => { setEditing(undefined); setFormOpen(true); };
    const openEdit = (m: WebApiMemberDtoMemberDto) => { setEditing(m); setFormOpen(true); };

    const handleDelete = async (m: WebApiMemberDtoMemberDto) => {
        setBusyId(m.memberId!);
        try {
            await MemberService.DeleteMember(m.memberId!);
            props.loadMembers?.();
        } catch {
            message.error(GetText("EditMember_ErrorSaving") || "Could not delete.");
        }
        setBusyId(null);
    };

    const columns: ColumnsType<WebApiMemberDtoMemberDto> = [
        {
            title: GetText("MemberList_NameColumn") || "Namn",
            key: "name",
            render: (_, m) => (
                <span style={{ fontWeight: 500 }}>{highlight(familyDisplayName(m) || "–", props.searchLower)}</span>
            ),
        },
        {
            title: GetText("MemberList_EmailColumn"),
            key: "email",
            render: (_, m) => (
                <span>
                    {highlight(m.email, props.searchLower)}
                    {wantsNoEmail(m) && (
                        <div style={{ marginTop: 2 }}>
                            <Tag style={{ marginInlineEnd: 0, color: "#8c6d00", background: "#fffbe6", borderColor: "#ffe58f" }}>
                                {GetText("MemberList_FamilyEmailOptOut")}
                            </Tag>
                        </div>
                    )}
                </span>
            ),
        },
        {
            title: GetText("MemberList_PhoneColumn"),
            key: "phone",
            render: (_, m) => highlight(m.phoneNumber, props.searchLower),
        },
        {
            title: GetText("MemberList_GroupsColumn"),
            key: "groups",
            render: (_, m) => (m.groupNames && m.groupNames.length > 0 ? m.groupNames.join(", ") : ""),
        },
        ...(props.canEdit
            ? [{
                title: GetText("MemberList_ActionColumn"),
                key: "action",
                width: 90,
                render: (_: unknown, m: WebApiMemberDtoMemberDto) => (
                    <Space size="middle">
                        <EditOutlined
                            data-testid="family-member-edit"
                            style={{ fontSize: "20px", cursor: "pointer" }}
                            onClick={() => openEdit(m)}
                        />
                        <Popconfirm
                            title={GetText("MemberList_DeleteMemberConfirmMessage")}
                            okText={GetText("Common_Yes")}
                            cancelText={GetText("Common_No")}
                            onConfirm={() => handleDelete(m)}
                        >
                            <DeleteOutlined
                                data-testid="family-member-delete"
                                style={{ fontSize: "20px", color: busyId === m.memberId ? "#bfbfbf" : "red", cursor: "pointer" }}
                            />
                        </Popconfirm>
                    </Space>
                ),
            }]
            : []),
    ];

    return (
        <div data-testid="family-member-sublist" style={{ padding: "2px 0" }}>
            {!props.hideHeader && (
                <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 4 }}>
                    <TeamOutlined style={{ marginRight: 6 }} />
                    {GetText("MemberList_FamilyMembersLabel")}
                </div>
            )}

            <Table
                size="small"
                rowKey={(m) => m.memberId!}
                dataSource={props.familyMembers}
                pagination={false}
                scroll={{ x: "max-content" }}
                locale={{ emptyText: GetText("MemberList_EmptyMessage") }}
                rowClassName={(m) => (rowMatches(m, props.searchLower) ? "invalid-email-row" : "")}
                columns={columns}
            />

            {props.canEdit && (
                <>
                    <Button
                        type="link"
                        size="small"
                        icon={<PlusOutlined />}
                        style={{ padding: "4px 8px" }}
                        onClick={openAdd}
                        data-testid="family-member-add"
                    >
                        {GetText("FamilyMemberForm_AddTitle") || "Lägg till familjemedlem"}
                    </Button>

                    <FamilyMemberForm
                        open={formOpen}
                        onClose={() => setFormOpen(false)}
                        onSaved={() => props.loadMembers?.()}
                        primaryMemberId={props.primaryMember.memberId!}
                        member={editing}
                        groups={props.groups}
                    />
                </>
            )}
        </div>
    );
};

export default FamilyMemberSubList;
